/* Per-piece cargo editor + weight math — extracted from newquote.js so the
   SmartQuote review modal renders the exact same table. Pure props: the
   caller owns the pieces array and the units; unit toggles (KG/LBS, CM/INCH)
   stay with the caller. Math mirrors the customer-portal QuoteForm
   calculation (templates/ELCAPP.html) so the chargeable kg lines up exactly
   with what the customer would see: chargeable = ceil(max(actual, vol)) to
   whole kg (2.65 → 3), matching the server's _coerce_charge_weight. */
import { useMemo, C } from '../shared/ui.js';
import { LBS_TO_KG, IN_TO_CM, VOL_DIVISOR_CM, ceilKg } from './helpers.js';

const newPiece = (i = 0) => ({ id: Date.now() + i, qty: 1, wt: '', L: '', W: '', H: '' });

const toKg = (w, wtUnit) => wtUnit === 'LBS' ? (parseFloat(w) || 0) * LBS_TO_KG : (parseFloat(w) || 0);
const toCm = (d, dimUnit) => dimUnit === 'INCH' ? (parseFloat(d) || 0) * IN_TO_CM : (parseFloat(d) || 0);

const pieceVolKg = (p, dimUnit) =>
  toCm(p.L, dimUnit) * toCm(p.W, dimUnit) * toCm(p.H, dimUnit) / VOL_DIVISOR_CM * (parseInt(p.qty) || 1);
// wtMode 'EACH' (default): wt is per piece, actual = wt × qty.
// wtMode 'TOTAL': wt is already the line total — qty only drives volumetric
// weight + piece counts. Dims stay per piece in both modes.
const pieceActKg = (p, wtUnit, wtMode = 'EACH') =>
  toKg(p.wt, wtUnit) * (wtMode === 'TOTAL' ? 1 : (parseInt(p.qty) || 1));

const computeTotals = (pieces, wtUnit, dimUnit, wtMode = 'EACH') => {
  let actualKg = 0, volKg = 0;
  for (const p of pieces) { actualKg += pieceActKg(p, wtUnit, wtMode); volKg += pieceVolKg(p, dimUnit); }
  const totalPcs = pieces.reduce((a, p) => a + (parseInt(p.qty) || 0), 0);
  return {
    pcs:    totalPcs,
    actual: Math.round(actualKg * 100) / 100,
    volume: Math.round(volKg * 100) / 100,
    // The billed chargeable weight — whole kg, matching the server rule.
    chargeable: ceilKg(Math.max(actualKg, volKg)),
  };
};

const PiecesEditor = ({ pieces, setPieces, wtUnit, dimUnit, wtMode = 'EACH', compact = false }) => {
  const totals = useMemo(() => computeTotals(pieces, wtUnit, dimUnit, wtMode), [pieces, wtUnit, dimUnit, wtMode]);

  const updPiece = (id, k, v) => setPieces(p => p.map(x => x.id === id ? { ...x, [k]: v } : x));
  const addPiece = () => setPieces(p => [...p, newPiece()]);
  const delPiece = id => setPieces(p => p.length > 1 ? p.filter(x => x.id !== id) : p);

  // Every header and cell is centred (Luke 2026-10-19).
  // compact: narrower columns, no spacer — fits a ~650px card (triage modal).
  // Two read-only auto columns per row: TOTAL WT (line actual, EACH/TOTAL-
  // aware) and VOL WT.
  const grid    = compact ? '46px 100px minmax(160px,1fr) 76px 76px 30px'
                          : '60px 150px 340px 24px 96px 96px 36px';   // fixed spacer: the row is one centred block (Luke 2026-10-19 — a 1fr spacer split it in the 1200px SpotRate card)
  const autoIdx = compact ? new Set([3, 4]) : new Set([4, 5]);
  const wtSfx   = wtMode === 'TOTAL' ? ' · TOTAL' : '';
  const heads   = compact
    ? [`QTY`, `WT (${wtUnit}${wtSfx})`, `L × W × H (${dimUnit})`, `TOT WT`, `VOL WT`, '']
    : [`QTY`, `WEIGHT (${wtUnit}${wtSfx})`, `DIMENSIONS — L × W × H (${dimUnit})`, ``, `TOTAL WT (${wtUnit})`, `VOL WT (${wtUnit})`, ''];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: grid, justifyContent: 'center', gap: 10, marginBottom: 6, padding: '0 2px' }}>
        {heads.map((h, i) => {
          const isAuto = autoIdx.has(i);
          return (
            <div key={i} style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: isAuto ? '#cbd5e1' : C.textD, fontStyle: isAuto ? 'italic' : 'normal', textAlign: 'center' }}>{h}{isAuto ? <> <span style={{ fontWeight: 600, opacity: .85 }}>· auto</span></> : ''}</div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {pieces.map((p, i) => {
          const actKg = pieceActKg(p, wtUnit, wtMode);
          const volKg = pieceVolKg(p, dimUnit);
          const isVol = volKg > actKg;
          const volWt = (wtUnit === 'LBS' ? volKg * 2.20462 : volKg).toFixed(1);
          const totWt = (wtUnit === 'LBS' ? actKg * 2.20462 : actKg).toFixed(1);
          const dimInputStyle = {padding:'8px 6px',fontSize:13,textAlign:'center',width:'100%',minWidth:0};
          const dimX = <span style={{ fontSize: 12, color: C.textD, fontWeight: 600, flexShrink: 0 }}>×</span>;
          return (
            <div key={p.id} style={{ display: 'grid', gridTemplateColumns: grid, justifyContent: 'center', gap: 10, alignItems: 'center', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)', borderRadius: 6, padding: '2px' }}>
              <input type="number" min="1" value={p.qty}
                     onChange={e => updPiece(p.id, 'qty', e.target.value)}
                     style={{ textAlign: 'center', padding: '8px 6px', fontSize: 13 }} />
              <input type="number" step="0.1" placeholder="0.0" value={p.wt}
                     onChange={e => updPiece(p.id, 'wt', e.target.value)}
                     style={{ padding: '8px 10px', fontSize: 13, textAlign: 'center' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <input type="number" step="0.1" placeholder="L" value={p.L}
                       onChange={e => updPiece(p.id, 'L', e.target.value)}
                       style={dimInputStyle} />
                {dimX}
                <input type="number" step="0.1" placeholder="W" value={p.W}
                       onChange={e => updPiece(p.id, 'W', e.target.value)}
                       style={dimInputStyle} />
                {dimX}
                <input type="number" step="0.1" placeholder="H" value={p.H}
                       onChange={e => updPiece(p.id, 'H', e.target.value)}
                       style={dimInputStyle} />
              </div>
              {!compact && <div />}
              <div style={{ textAlign: 'center', fontSize: 12, fontFamily: 'JetBrains Mono,monospace', color: C.textM, background: '#f1f5f9', border: `1px solid ${C.brd}`, borderRadius: 4, padding: '8px 6px' }}>{totWt}</div>
              <div style={{ textAlign: 'center', fontSize: 12, fontFamily: 'JetBrains Mono,monospace', color: isVol ? C.goldL : C.textM, fontWeight: isVol ? 700 : 400, background: '#f1f5f9', border: `1px solid ${C.brd}`, borderRadius: 4, padding: '8px 6px' }}>{volWt}</div>
              <button onClick={() => delPiece(p.id)} disabled={pieces.length === 1}
                      style={{ width: 28, height: 28, borderRadius: 6, background: 'transparent', border: `1px solid ${C.brd}`, color: C.textD, cursor: pieces.length === 1 ? 'not-allowed' : 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pieces.length === 1 ? .3 : 1, flexShrink: 0 }}
                      title="Remove">×</button>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.brd}` }}>
        <button onClick={addPiece}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px dashed ${C.brd}`, color: C.blue, cursor: 'pointer', padding: '7px 16px', borderRadius: 6, fontSize: 12, fontFamily: 'Barlow,sans-serif', transition: 'border-color .15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.brd}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add Piece
        </button>
        <div style={{ display: 'flex', gap: compact ? 14 : 24, fontSize: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Total Pieces</div>
            <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, fontSize: 16 }}>{totals.pcs} <span style={{ fontSize: 11, color: C.textD, fontWeight: 400 }}>pcs</span></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: C.textD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Actual Weight</div>
            <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, fontSize: 16 }}>{ceilKg(wtUnit === 'LBS' ? totals.actual * 2.20462 : totals.actual)} <span style={{ fontSize: 11, color: C.textD, fontWeight: 400 }}>{wtUnit}</span></div>
          </div>
          <div style={{ textAlign: 'right', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 8, padding: '6px 14px' }}>
            <div style={{ fontSize: 10, color: C.blue, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Chargeable Weight</div>
            <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, fontSize: 16, color: C.blue }}>{Math.round((wtUnit === 'LBS' ? totals.chargeable * 2.20462 : totals.chargeable))} <span style={{ fontSize: 11, fontWeight: 400 }}>{wtUnit}</span></div>
          </div>
        </div>
      </div>
    </>
  );
};

export { PiecesEditor, computeTotals, pieceActKg, pieceVolKg, newPiece };
